import { useAuth0 } from "@auth0/auth0-react";
import { useSessionStorageValue } from "@react-hookz/web";
import * as Sentry from "@sentry/react";
import i18n from "i18next";
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Loader } from "~/components/ui/loader";
import { useJoinBrandMutation } from "~/graphql/graphql-new.gen";
import { getI18nLanguageFromISOLanguage } from "~/libs/i18n";
import type { BrandMatch } from "~/routes/root";
import { hasBrandAccess } from "~/utils/brandsAccess";

export const ProtectedRoute = ({
  children,
}: {
  children: React.ReactElement;
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);

  const { getAccessTokenSilently, logout, isLoading, isAuthenticated, user } =
    useAuth0();

  const { brandId = "" } = useParams<BrandMatch>();
  const { value: inviteCode, set: setInviteCode } =
    useSessionStorageValue<string>("inviteCode");

  const { mutateAsync: joinBrandMutation, error: joinBrandError } =
    useJoinBrandMutation();
  const [permissionLoading, setPermissionLoading] = useState(false);

  useEffect(() => {
    if (queryParams.has("invite-code")) {
      setInviteCode(queryParams.get("invite-code") as string);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const preferredLanguage = user?.[
        "https://appchoose.io/claims/preferredLanguage"
      ] as string | undefined;
      if (preferredLanguage) {
        const languageToSet = getI18nLanguageFromISOLanguage(preferredLanguage);
        if (languageToSet !== i18n.language) {
          i18n.changeLanguage(languageToSet);
        }
      }

      const handlePermission = async () => {
        setPermissionLoading(true);
        if (queryParams.has("invite-code") || inviteCode) {
          if (
            user?.["https://appchoose.io/claims/isInternalUser"] === false &&
            !hasBrandAccess(user, brandId || "")
          ) {
            await joinBrandMutation({
              brandId: brandId || "",
              inviteCode: queryParams.has("invite-code")
                ? (queryParams.get("invite-code") as string)
                : (inviteCode ?? ""),
            });
            setInviteCode("");
            await getAccessTokenSilently({ cacheMode: "off" });
          }
        } else {
          if (user?.["https://appchoose.io/claims/isInternalUser"] === false) {
            if (
              !hasBrandAccess(user, brandId || "") &&
              !location.pathname.includes(`${brandId}/limited-access`)
            ) {
              Sentry.captureException({
                name: "noBrandAccessError",
                message: JSON.stringify({
                  brandAccess:
                    user?.["https://appchoose.io/claims/brandsAccess"],
                }),
              });
              navigate(`/${brandId}/limited-access`, { replace: true });
            }
            if (
              hasBrandAccess(user, brandId || "") &&
              location.pathname.includes(`${brandId}/limited-access`)
            ) {
              navigate(`/${brandId}/home`, { replace: true });
            }
          }
        }

        setPermissionLoading(false);
      };

      handlePermission();

      Sentry.setUser({ id: user?.sub, email: user?.email });
    } else {
      Sentry.setUser(null);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (joinBrandError) {
      Sentry.captureException(joinBrandError);
      navigate(`/${brandId}/limited-access`, { replace: true });
    }
  }, [joinBrandError]);

  if (isLoading || permissionLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader className="size-8" />
      </div>
    );
  }

  console.log("user", user);

  if (isAuthenticated) {
    return children;
  }

  sessionStorage.setItem(
    "redirectUri",
    `"${location.pathname}/${location.search}${location.hash}"`,
  );

  logout({
    logoutParams: {
      returnTo: `${window.location.origin}/logout-successful${location.search}`,
    },
  });

  return (
    <div className="flex h-screen items-center justify-center">
      <Loader className="size-8" />
    </div>
  );
};

ProtectedRoute.displayName = "ProtectedRoute";
